/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import { css, useTheme } from "@emotion/react";
import { Container, Icon, Link, Toggle } from "@coreai/component-library";
import exclamation from "../../../../assets/exclamation.svg";
import ResultSection from "./ResultSection";
import { useLanguage } from "LanguageContext";
import { useAnalysis } from "../FormComponents/AnalysisResultContext";
import {TFeedbackDetails} from "../../../../services/feedbackService";
import ScrutinizedWords from "./ScrutinizedWords";

const IconWrapper: React.FC<{ src: string; size?: string }> = ({
  src,
  size = "24px",
}) => <img src={src} alt="icon" style={{ width: size, height: size }} />;

interface DetailedProps {
  validAnalysis: any;
  invalidAnalysis: any;
  middleAnalysis: any;
  notApplicableAnalysis: any;
  resultData: any;
}

export interface AnalysisSummaryResult {
  index?: number;
  title: string;
  evaluation: string;
  feedbacks: Array<TFeedbackDetails>;
  id: string;
  content?: string;
}

export default function ComplianceReferenceCard(props: Readonly<DetailedProps>) {
  const {analysisContextResult} = useAnalysis();
  const theme = useTheme();
  const { t } = useLanguage();
  const [openAll, setOpenAll] = useState(false);
  const [showToggle, setShowToggle] = useState(false);

  const cardStyles = css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing["5xl"]};
    width: 100%;
    box-sizing: border-box;
    background-color: ${theme.colors.grey};
    padding: 30px;
  `;

  const iconStyle = (iconBgColor?: any) => css`
    color: #000;
    background-color: ${iconBgColor ? iconBgColor : ''};
    border-radius: 50%;
    padding: 4px;
  `;

  const containerStyle = css`
    display: flex;
    flex-direction: column;
    background-color: ${theme.colors.white};
    padding: ${theme.spacing["5xl"]};
    text-align: left;
    border-radius: ${theme.radius.sm};
    color: ${theme.colors.textMedium};

    @media (max-width: 768px) {
      padding: 24px;
    }
  `;

  const headerStyle = css`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: ${theme.spacing.xxxl};

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 10px;
    }
  `;

  const titleStyles = css`
    font-family: ${theme.typography.heading3_medium.fontFamily};
    font-size: ${theme.typography.heading3_medium.fontSize};
    font-weight: ${theme.typography.heading3_medium.fontWeight};
    display: flex;
    color: ${theme.typography.heading3_medium.colors};
  `;

  const linkStyles = css`
    font-family: ${theme.typography.body_semiBold.fontFamily};
    font-size: ${theme.typography.body_semiBold.fontSize};
    font-weight: ${theme.typography.body_semiBold.fontWeight};
    text-decoration: none;
    color: ${theme.colors.primary};
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;

    @media (max-width: 768px) {
      align-self: flex-end;
    }
  `;

  const toggleWrapper = css`
    display: flex;
    justify-content: right;
    align-items: flex-end;
    gap: 10px;
    margin-top: 20px;
  `;

  const toggleStyle = css`
    align-self: flex-start;
  `;

  const toggleTitleStyle = css`
    font-family: ${theme.typography.subbody_medium.fontFamily};
    font-size: ${theme.typography.subbody_medium.fontSize};
    font-weight: 500;
    text-decoration: none;
    color: ${theme.colors.primary};
  `;

  const supportTextStyle = css`
    font-family: ${theme.typography.subbody_medium.fontFamily};
    font-size: ${theme.typography.subbody_medium.fontSize};
    font-weight: 400;
    text-decoration: none;
    color: ${theme.colors.textMedium};
  `;

  const toggleAllAccordion = () => {
    setOpenAll((prev) => !prev);
  };

const _totalWordList = props?.resultData?.risk_assessment?.[0]?.response ?? [];

const _validWordList = Array.isArray(_totalWordList)
  ? _totalWordList.filter((_item: any) => _item?.polarization_level?.trim() !== "")
  : [];

  return (
    <Container variant="column" css={containerStyle}>
      <div css={headerStyle}>
        <div css={titleStyles}>
          {t("analyse_result_detailedAnalysis_title", null, analysisContextResult?.locale_code)}
        </div>
        <Link css={linkStyles} onClick={toggleAllAccordion}>
          {openAll
            ? t("analyse_result_detailedAnalysis_close_all", null, analysisContextResult?.locale_code)
            : t("analyse_result_detailedAnalysis_open_all", null, analysisContextResult?.locale_code)}
          {openAll ? (
            <Icon name="chevron-up" size="sm" />
          ) : (
            <Icon name="chevron-down" size="sm" />
          )}
        </Link>
      </div>

      <div css={cardStyles}>
        <ResultSection
          title={t("analyse_result_detailedAnalysis_risk", null, analysisContextResult?.locale_code)}
          icon={ <img src={exclamation} alt="alert" css={() => iconStyle(theme.colors.red)} />}
          bgColor={theme.colors.red}
          items={props.invalidAnalysis}
          openAll={openAll}
          showToggle={showToggle}
          analysisType="invalid"
        />
        <ResultSection
          title={t("analyse_result_detailedAnalysis_requesting", null, analysisContextResult?.locale_code)}
          icon={<Icon name="question" size="lg" css={() => iconStyle()} />}
          bgColor={theme.colors.yellow}
          items={props.middleAnalysis}
          openAll={openAll}
          showToggle={showToggle}
          analysisType="middle"
        />
        <ResultSection
          title={t("analyse_result_detailedAnalysis_compliant", null, analysisContextResult?.locale_code)}
          icon={<Icon name="check" size="lg" css={() => iconStyle()} />}
          bgColor={theme.colors.green}
          items={props.validAnalysis}
          openAll={openAll}
          showToggle={showToggle}
          analysisType="valid"
        />
        <ResultSection
          title={t("analyse_result_detailedAnalysis_not_applicable", null, analysisContextResult?.locale_code)}
          icon={<Icon name="minus" size="xl" css={() => iconStyle("#EAEEF0")} />}
          bgColor={theme.colors.grey}
          items={props.notApplicableAnalysis}
          openAll={openAll}
          showToggle={showToggle}
          analysisType="not-applicable"
        />
        </div>
        <div css={toggleWrapper}>
        <div css={toggleStyle}>
          <Toggle 
            checked={showToggle}
            onCheckedChange={() =>setShowToggle((prev) => !prev)}
            variant="brand"
            size="sm"
            // text="Details for green"
            // supportingText="Show rationale for Compliant & Not applicable."
          />
        </div>
        <div>            
          <div css={toggleTitleStyle}>{t("analyse_result_detailedAnalysis_toggle_text", null, analysisContextResult?.locale_code)}</div>
          <div css={supportTextStyle}>{t("analyse_result_detailedAnalysis_toggle_Support_text", null, analysisContextResult?.locale_code)}</div>
        </div>
      </div>
      {props?.resultData?.political_risk_ruleset_id && (
        <ScrutinizedWords resultData={props?.resultData}/>
      )}
    </Container>
  );
}
